import { FormikProps, getIn } from 'formik'
import React, { FC, useEffect, useId } from 'react'
import { DateInput, Label } from '..'
import { FontSize, formatDateToInput, validateDate } from '../../../_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'

interface IFormDateInput {
  label?: string
  name: string
  placeholder?: string
  formik: FormikProps<any>
  isDisabled?: boolean
  isRequired?: boolean
  className?: string
  minDate?: string
  maxDate?: string | Date
  labelSize?: FontSize
  infoText?: string
  highlight?: boolean
  entity?: string
}

export const FormDateInput: FC<IFormDateInput> = ({
  label,
  name,
  placeholder,
  formik,
  isDisabled = false,
  className = '',
  isRequired = false,
  minDate,
  maxDate,
  infoText = '',
  labelSize,
  highlight,
  entity,
}) => {
  const { t } = useT()
  const errorMessage = getIn(formik.errors, name, null)
  const touched = getIn(formik.touched, name, '')

  const resolvedLabel = label ?? (entity ? t(`${entity}.label`) : undefined)

  // Id estável para associação programática label<->campo, no mesmo padrão que o `FormInput` já
  // segue (`FormInput.tsx:50-51`). `useId` evita colisão quando o mesmo `name` aparece duas vezes na
  // página. Aditivo: antes o rótulo do campo de data não apontava para input nenhum.
  const reactId = useId()
  const inputId = `${name}-${reactId}`

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    formik.setFieldValue(name, e.target.value)

  // Data inválida ou fora de min/max vira erro do Formik e bloqueia o submit, sem repetir a regra
  // em cada schema.
  const { registerField, unregisterField } = formik
  const max = maxDate ? formatDateToInput(maxDate) : undefined
  useEffect(() => {
    registerField(name, {
      validate: (valor?: string) => validateDate(valor ?? '', minDate, max) || undefined,
    })
    return () => unregisterField(name)
  }, [name, minDate, max, registerField, unregisterField])

  return (
    <div className={`${className}`}>
      {resolvedLabel && (
        <Label
          className='mb-2'
          htmlFor={inputId}
          isRequired={isRequired}
          size={labelSize}
          infoText={infoText}
          highlight={highlight}
        >
          {resolvedLabel}
        </Label>
      )}
      <DateInput
        {...formik.getFieldProps(name)}
        id={inputId}
        placeholder={placeholder}
        onChange={handleChange}
        disabled={formik.isSubmitting || isDisabled}
        maxDate={maxDate}
        isInvalid={!!errorMessage && !!touched}
        minDate={minDate}
        errorMessage={touched && errorMessage ? errorMessage : undefined}
      />
    </div>
  )
}
